import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { createClaimApi } from "@shared/claims/ClaimApiClient";
import { useClaimFlow } from "@shared/claims/useClaimFlow";
import { useTokenBalancesActions } from "@shared/wallet/hooks/useTokenBalances";
import { API_URLS } from "@solomon-apps/shared";
import { Button, Text } from "@solomon-labs/design/components/ui";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef } from "react";

import { yaasWalletQueryKey } from "@/hooks/yaas/queryKeys";
import { useRetailClaimSummary } from "@/hooks/yaas/useRetailClaimSummary";

/** Claim availability is independent of previously paid rewards and current delivery mode. */
export function RetailClaimButton({
    address,
    deEmphasized = false
}: {
    address: string;
    deEmphasized?: boolean;
}): React.ReactElement | null {
    const summary = useRetailClaimSummary(address);
    const queryClient = useQueryClient();
    const { refetch: refreshBalances } = useTokenBalancesActions();
    const refreshedClaim = useRef<string | null>(null);
    const api = useMemo(
        () =>
            createClaimApi({
                audience: "main",
                account: address,
                scopeKey: `main:${API_URLS.YAAS}:${address}`,
                fetch: (path, init) => fetch(`${API_URLS.YAAS}${path}`, init)
            }),
        [address]
    );
    const flow = useClaimFlow(api);
    const analytics = useProductJourney("claim", "rewards_summary", address);
    // An attempt exists that has not settled. Folded into the same busy
    // label rather than getting its own: from the user's side "the claim
    // is going through" is one state, whether the work is in this tab or
    // onchain. It used to read "Finishing Claim", which described our
    // internals rather than their situation.
    const settling = flow.request?.attempts.some((attempt) =>
        ["submitted", "confirming", "unknown"].includes(attempt.state)
    );
    const inFlight = flow.busy || settling;
    // Preparation reserves the rewards. A rejected signature must still allow
    // the same prepared request to resume after availability excludes that reservation.
    const resumable =
        flow.request?.attempts.some(
            (attempt) =>
                attempt.state === "prepared" &&
                attempt.requiredAction === "sign_and_submit"
        ) === true;
    const hasClaimable = summary.data?.hasClaimable === true || resumable;
    const canClaim =
        (summary.data?.canClaim === true || resumable) && !summary.isError;
    const completed = flow.request?.state === "completed";
    const claimRequestUuid = flow.request?.claimRequestUuid;
    const { refetch } = summary;
    useEffect(() => {
        if (!completed || !claimRequestUuid) return;
        const key = `${address}:${claimRequestUuid}`;
        if (refreshedClaim.current === key) return;
        refreshedClaim.current = key;
        void refetch();
        void queryClient.invalidateQueries({
            queryKey: yaasWalletQueryKey(address)
        });
        void queryClient.invalidateQueries({
            queryKey: ["yaas", "distributions", address]
        });
        void queryClient.invalidateQueries({
            queryKey: ["jupiter-holdings", address]
        });
        refreshBalances();
    }, [
        address,
        claimRequestUuid,
        completed,
        queryClient,
        refetch,
        refreshBalances
    ]);

    useProductObservation(analytics, "state_viewed", {
        is_available: !inFlight && flow.ready && canClaim,
        reason: !hasClaimable
            ? "no_claimable_rewards"
            : !flow.ready
              ? "not_ready"
              : inFlight
                ? "pending"
                : "available"
    });
    if (!hasClaimable && !inFlight && !flow.error) {
        if (summary.isError || summary.data?.status === "unavailable")
            return (
                <Text variant="small" role="alert">
                    Could not check claimable rewards.{" "}
                    <button type="button" onClick={() => void refetch()}>
                        Retry
                    </button>
                </Text>
            );
        return null;
    }

    return (
        <div className="md:mt-12 flex flex-col items-center gap-3">
            <Button
                variant={deEmphasized ? "sunken" : "raised"}
                outline={deEmphasized}
                interaction="light-bar"
                size="lg"
                // Wider than lg's own px-6. This is the one earned action
                // in the hero, so it gets the extra inline room rather
                // than sitting at the same width as the de-emphasised
                // Swap CTA opposite it.
                className="px-10 md:px-10"
                disabled={inFlight || !flow.ready || !canClaim}
                status={inFlight ? "pending" : undefined}
                onClick={() => {
                    analytics.send("requested");
                    void flow.claim();
                }}
                data-sim-cursor-key="claim"
                // `labels` sizes the label cell to the widest member once,
                // so "Claim" reserves the width of "Claiming..." and the
                // button never reflows when the swap happens.
                labels={{ idle: "Claim", busy: "Claiming..." }}
                labelKey={inFlight ? "busy" : "idle"}
            />
            {/* No standing explainer. The button carries its own state,
                including disabled-while-unready: reaching this surface
                already required a connected wallet, so telling the user
                to connect one is noise. Only a failure earns a line. */}
            {(summary.isError || summary.data?.status === "unavailable") && (
                <Text variant="small" role="alert">
                    Could not check claimable rewards.{" "}
                    <button type="button" onClick={() => void refetch()}>
                        Retry
                    </button>
                </Text>
            )}
            {summary.data?.preparationEnabled === false && (
                <Text variant="small" role="alert">
                    Claims are temporarily unavailable.
                </Text>
            )}
            {flow.error && (
                <Text variant="small" role="alert" className="text-error-base">
                    {flow.error}
                </Text>
            )}
        </div>
    );
}
